import { startAuthentication } from '@simplewebauthn/browser';
import createDebug from 'debug';
import { createContext, useContext, useEffect, useState } from 'react';
import {
	logout as apiLogout,
	getCurrentUser,
	getPasskeyOptions,
	loginWithPasskey,
} from '../api/auth.ts';
import { setAuthToken, clearAuthToken } from '../api/client.ts';

const debug = createDebug('app:auth-context');

// =============================================================================
// Types
// =============================================================================

export interface User {
	id: number;
	username: string;
	display_name: string;
	permissions: string[];
}

export interface AuthContextValue {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: (username: string) => Promise<void>;
	logout: () => Promise<void>;
	refreshUser: () => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	/**
	 * Refresh user data from /api/auth/me
	 * Called on mount and after successful login
	 */
	async function refreshUser() {
		debug('refreshUser: fetching current user');
		const result = await getCurrentUser();

		if (result.user) {
			debug('refreshUser: user authenticated - %s', result.user.username);
			setUser(result.user);
		} else {
			debug('refreshUser: not authenticated - %s', result.error || 'unknown');
			setUser(null);
		}
	}

	/**
	 * Login with passkey authentication
	 * 1. Get auth options from /api/auth/passkey/options
	 * 2. Call navigator.credentials.get() with @simplewebauthn/browser
	 * 3. Send response to /api/auth/passkey/login
	 * 4. Store JWT in localStorage and user data
	 */
	async function login(username: string) {
		debug('login: starting passkey auth for %s', username);

		try {
			// STEP 1: Get auth options from server
			const optionsResult = await getPasskeyOptions(username);
			if (optionsResult.error || !optionsResult.options) {
				throw new Error(optionsResult.error || 'Failed to get passkey options');
			}

			debug('login: got passkey options, starting authentication');

			// STEP 2: Start passkey authentication with browser
			const credential = await startAuthentication({
				optionsJSON: optionsResult.options,
			});

			debug('login: passkey credential obtained, sending to server');

			// STEP 3: Send credential to server for verification
			const loginResult = await loginWithPasskey(username, credential);

			if (!loginResult.success || !loginResult.user || !loginResult.token) {
				throw new Error(loginResult.error || 'Login failed');
			}

			debug('login: success - user %s authenticated', loginResult.user.username);

			// STEP 4: Store JWT in localStorage and user data
			setAuthToken(loginResult.token);
			setUser(loginResult.user);
		} catch (err) {
			debug('login: error - %o', err);
			// Re-throw to let calling code handle the error
			throw err instanceof Error ? err : new Error('Login failed');
		}
	}

	/**
	 * Logout the current user
	 * Clears JWT from localStorage and user state
	 */
	async function logout() {
		debug('logout: clearing auth state');

		try {
			await apiLogout();
			clearAuthToken();
			setUser(null);
			debug('logout: success');
		} catch (err) {
			debug('logout: error - %o', err);
			// Always clear local state even if API call fails
			clearAuthToken();
			setUser(null);
		}
	}

	// Check auth status on mount
	useEffect(() => {
		refreshUser().finally(() => setIsLoading(false));
		 
	}, []); // Run once on mount

	const value: AuthContextValue = {
		user,
		isAuthenticated: user !== null,
		isLoading,
		login,
		logout,
		refreshUser,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Use auth context in components
 * Throws error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
}
